import { useCallback, useEffect, useState } from "react";

import api from "@/api/axios";
import { API } from "@/App";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";

const inr = (n) =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("en-IN").format(n);

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN") : "—");

const errorText = (ex, fallback) =>
  ex?.response?.data?.detail || ex?.response?.data?.message || fallback;

/**
 * Manages one person's dated hourly rates: add a rate from a chosen date,
 * correct a past one, or remove an entry. Every timesheet is costed at the
 * rate in force on its own date, so changes here re-price the periods they
 * cover and nothing else.
 */
const RateHistoryEditor = ({ userId, userName, onChanged }) => {
  const { toast } = useToast();

  const [history, setHistory] = useState([]);
  const [currentRate, setCurrentRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ rate: "", effective_from: "" });

  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState({ rate: "", effective_from: "" });

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/users/${userId}/rate-history`);
      setHistory(data.history || []);
      setCurrentRate(data.current_rate ?? null);
      setErr("");
    } catch (ex) {
      setErr(errorText(ex, "Failed to load rate history"));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const afterWrite = (data, message) => {
    setHistory(data.history || []);
    setCurrentRate(data.current_rate ?? null);
    toast({ title: "Saved", description: message });
    // The person's current rate may have moved, so the parent list is stale.
    if (onChanged) onChanged(data);
  };

  const handleAdd = async () => {
    if (!addDraft.rate || !addDraft.effective_from) {
      toast({
        variant: "destructive",
        title: "Missing details",
        description: "Enter both a rate and the date it takes effect.",
      });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`${API}/users/${userId}/rate-history`, {
        rate: Number(addDraft.rate),
        effective_from: addDraft.effective_from,
      });
      afterWrite(data, `Rate of ₹${inr(Number(addDraft.rate))}/hr applies from ${fmtDate(addDraft.effective_from)}.`);
      setAdding(false);
      setAddDraft({ rate: "", effective_from: "" });
    } catch (ex) {
      toast({ variant: "destructive", title: "Could not add rate", description: errorText(ex, "An unexpected error occurred.") });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async (entryId) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`${API}/users/${userId}/rate-history/${entryId}`, {
        rate: Number(editDraft.rate),
        effective_from: editDraft.effective_from,
      });
      afterWrite(data, "Rate entry updated.");
      setEditingId(null);
    } catch (ex) {
      toast({ variant: "destructive", title: "Could not update rate", description: errorText(ex, "An unexpected error occurred.") });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entry) => {
    const confirmed = window.confirm(
      `Remove the ₹${inr(entry.rate)}/hr rate starting ${fmtDate(entry.effective_from)}?\n\n` +
        `Timesheets in that period will be re-costed at the rate before it.`
    );
    if (!confirmed) return;

    setBusy(true);
    try {
      const { data } = await api.delete(`${API}/users/${userId}/rate-history/${entry.id}`);
      afterWrite(data, "Rate entry removed.");
    } catch (ex) {
      toast({ variant: "destructive", title: "Could not remove rate", description: errorText(ex, "An unexpected error occurred.") });
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditDraft({
      rate: String(entry.rate ?? ""),
      effective_from: entry.effective_from || "",
    });
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading rate history…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold">Hourly rate history</h4>
          <p className="text-xs text-muted-foreground">
            {userName ? `${userName} is ` : "Currently "}on{" "}
            <span className="font-mono">₹{inr(currentRate)}/hr</span>. Timesheets are costed at
            the rate in force on each day.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || adding}
          onClick={() => setAdding(true)}
        >
          <Plus className="mr-1 h-4 w-4" /> Add rate
        </Button>
      </div>

      {err && <div className="text-sm text-destructive">{err}</div>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Effective from</TableHead>
              <TableHead>Until</TableHead>
              <TableHead>Rate (INR/hr)</TableHead>
              <TableHead>Change</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adding && (
              <TableRow className="bg-muted/40">
                <TableCell>
                  <Input
                    type="date"
                    autoFocus
                    value={addDraft.effective_from}
                    onChange={(e) => setAddDraft({ ...addDraft, effective_from: e.target.value })}
                  />
                </TableCell>
                <TableCell className="text-muted-foreground">—</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Rate"
                    value={addDraft.rate}
                    onChange={(e) => setAddDraft({ ...addDraft, rate: e.target.value })}
                  />
                </TableCell>
                <TableCell className="text-muted-foreground">—</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button type="button" size="icon" variant="ghost" disabled={busy} onClick={handleAdd}>
                    <Check className="h-4 w-4" />
                    <span className="sr-only">Save new rate</span>
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setAdding(false);
                      setAddDraft({ rate: "", effective_from: "" });
                    }}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Cancel</span>
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {history.map((h) => {
              const isEditing = editingId === h.id;
              return (
                <TableRow key={h.id || `implicit-${h.effective_from}`}>
                  <TableCell>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editDraft.effective_from}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, effective_from: e.target.value })
                        }
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        {fmtDate(h.effective_from)}
                        {h.is_current && <Badge variant="default">Current</Badge>}
                        {h.is_scheduled && <Badge variant="secondary">Scheduled</Badge>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {h.effective_to ? fmtDate(h.effective_to) : "—"}
                  </TableCell>
                  <TableCell className="font-mono">
                    {isEditing ? (
                      <Input
                        type="number"
                        min="0"
                        value={editDraft.rate}
                        onChange={(e) => setEditDraft({ ...editDraft, rate: e.target.value })}
                      />
                    ) : (
                      `₹${inr(h.rate)}`
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {h.increment === null || h.increment === undefined
                      ? h.change_type
                      : `${h.increment > 0 ? "+" : ""}${inr(h.increment)}${
                          h.increment_pct === null || h.increment_pct === undefined
                            ? ""
                            : ` (${h.increment_pct > 0 ? "+" : ""}${h.increment_pct}%)`
                        }`}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {isEditing ? (
                      <>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => handleSaveEdit(h.id)}
                        >
                          <Check className="h-4 w-4" />
                          <span className="sr-only">Save</span>
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => setEditingId(null)}
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Cancel</span>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          // An implied row has no stored entry behind it yet, so
                          // there is nothing addressable to edit or delete.
                          disabled={busy || !h.id}
                          title={h.id ? "Edit this rate" : "Save a dated rate first"}
                          onClick={() => startEdit(h)}
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          disabled={busy || !h.id || history.length < 2}
                          title={
                            history.length < 2
                              ? "A person must keep at least one rate"
                              : "Remove this rate"
                          }
                          onClick={() => handleDelete(h)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Remove</span>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {!history.length && !adding && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No rates on record yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        Back-dating a rate re-costs every timesheet from that date until the next rate starts.
        Salary and project cost reports for those periods will change.
      </p>
    </div>
  );
};

export { RateHistoryEditor };
export default RateHistoryEditor;
